import { type CSSProperties } from "react";
import { getMapStopArrivalTimePillPresentation } from "../../../theme/DelayStatusColors";
import { type PopupContent } from "../vehicleSelection/vehicleSelection.types";

/** Default application primary color RGB values (ionic-primary: #11D39C) */
export const DEFAULT_PRIMARY_COLOR_R = 17;

export const DEFAULT_PRIMARY_COLOR_G = 211;

export const DEFAULT_PRIMARY_COLOR_B = 156;

/** Opacity values for vehicle color backgrounds */
export const VEHICLE_INFO_BACKGROUND_OPACITY = 0.05;

export const VEHICLE_INFO_HEADER_OPACITY = 0.4;
const DEFAULT_OPACITY = 0.3;

/** Badge and text dimensions */
export const VEHICLE_INFO_BADGE_MIN_WIDTH_PX = 35;

export const VEHICLE_INFO_BADGE_MAX_WIDTH_PX = 65;

/** Fixed height (px) shared by the trailing delay badge and follow toggle. */
export const VEHICLE_ISLAND_TRAILING_CONTROL_HEIGHT_PX = 30;

/** Shared height for trailing island controls (delay badge + follow button). */
export const vehicleIslandTrailingControlHeightStyle = { "height": `${ VEHICLE_ISLAND_TRAILING_CONTROL_HEIGHT_PX }px` } as const;

/** Square footprint for the follow toggle button. */
export const vehicleIslandFollowControlStyle = {
    ...vehicleIslandTrailingControlHeightStyle,
    "width": `${ VEHICLE_ISLAND_TRAILING_CONTROL_HEIGHT_PX }px`,
} as const;

/** Fixed height for symmetric island side controls (line badge and follow toggle). */
export const VEHICLE_ISLAND_SIDE_CONTROL_HEIGHT_PX = "22px";

/** Shared width/height for island line badge and follow toggle footprints. */
export const vehicleIslandSideControlStyle = {
    "minHeight": `${ VEHICLE_ISLAND_SIDE_CONTROL_HEIGHT_PX }`,
    "minWidth":  `${ VEHICLE_INFO_BADGE_MIN_WIDTH_PX }px`,
    "maxWidth":  `${ VEHICLE_INFO_BADGE_MAX_WIDTH_PX }px`,
} as const;

/** Tailwind row classes: side columns fixed height; center column uses full island height. */
export const VEHICLE_ISLAND_ROW_CLASS_NAME = "flex h-full w-full min-w-0 items-stretch";

/** Center text stack between symmetric island side controls (headsign + optional future subline). */
export const VEHICLE_ISLAND_TEXT_COLUMN_CLASS_NAME = "flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center gap-0 px-1 self-stretch";

/** Reserved viewport for a future second line in the center column (unused content for now). */
export const VEHICLE_ISLAND_CENTER_SUBLINE_VIEWPORT_CLASS = "h-[1.125rem] w-full shrink-0";

/** Island center subline typography (route type + original headsign). */
export const VEHICLE_ISLAND_CENTER_SUBLINE_TEXT_CLASS = "block w-full text-center text-xs font-medium leading-tight truncate";

/** Separator between center subline segments (middle dot). */
const VEHICLE_ISLAND_CENTER_SUBLINE_SEPARATOR = " · ";

/** France-specific route type codes mapped to display labels on the island subline. */
const ROUTE_TYPE_FRANCE_LABEL_BY_CODE: Readonly<Record<string, string>> = {
    "TER": "TER",
    "CTE": "TER",
    "OGO": "TGV OUIGO",
    "OUI": "TGV INOUI",
    "LYR": "TGV Lyria",
    "ICE": "TGV ICE",
    "IC":  "Intercité",
    "ICN": "Intercité",
};

/** France-specific route type codes that must not appear on the island subline. */
const ROUTE_TYPE_FRANCE_HIDDEN_CODES = new Set( [ "TRN", "TT" ] );

/** Trailing cluster to the right of the center column (delay badge + follow control). */
export const VEHICLE_ISLAND_TRAILING_CLUSTER_CLASS_NAME = "flex shrink-0 items-center self-center gap-1.5";

export const VEHICLE_INFO_TRIP_HEADING_MAX_WIDTH_PX = 180;

/** Island headsign typography (single primary row in the center column). */
export const VEHICLE_ISLAND_HEADSIGN_TEXT_CLASS = "block w-full text-center text-sm font-bold leading-tight truncate";

/** @deprecated Compact headsign when a center subline was shown; kept for callers migrating layout. */
export const VEHICLE_ISLAND_HEADSIGN_COMPACT_TEXT_CLASS = VEHICLE_ISLAND_HEADSIGN_TEXT_CLASS;

/** Shell classes for the trailing delay/advance badge (two stacked lines). */
export const VEHICLE_ISLAND_DELAY_BADGE_SHELL_CLASS = "flex w-fit max-w-full shrink-0 flex-col items-center justify-center rounded-md border box-border px-1 py-0.5";

/** Typography for each line inside the delay badge. */
export const VEHICLE_ISLAND_DELAY_BADGE_LINE_CLASS = "text-center text-[10px] font-semibold leading-none tabular-nums truncate max-w-full";

/** @deprecated Single-line delay badge text; use {@link VEHICLE_ISLAND_DELAY_BADGE_LINE_CLASS}. */
export const VEHICLE_ISLAND_DELAY_BADGE_TEXT_CLASS = VEHICLE_ISLAND_DELAY_BADGE_LINE_CLASS;

/** @deprecated Use {@link VEHICLE_ISLAND_DELAY_BADGE_TEXT_CLASS} — plain text delay line. */
export const VEHICLE_ISLAND_DELAY_TEXT_CLASS = "text-center text-[10px] font-semibold leading-none truncate tabular-nums";

/** Clipping viewport height for a single-line island headsign row (fits descenders at text-sm). */
export const VEHICLE_ISLAND_HEADSIGN_VIEWPORT_CLASS = "h-5 shrink-0";

/** @deprecated Legacy compact headsign viewport; identical to {@link VEHICLE_ISLAND_HEADSIGN_VIEWPORT_CLASS}. */
export const VEHICLE_ISLAND_HEADSIGN_COMPACT_VIEWPORT_CLASS = VEHICLE_ISLAND_HEADSIGN_VIEWPORT_CLASS;

/** Clipping viewport height for the island line badge label. */
export const VEHICLE_ISLAND_BADGE_VIEWPORT_CLASS = "h-3.5 shrink-0";

/** Sub-pixel tolerance when detecting CSS ellipsis on the island headsign. */
const VEHICLE_ISLAND_HEADSIGN_TRUNCATION_TOLERANCE_PX = 1;

/**
 * Returns whether a headsign text node is visually truncated (`truncate` / ellipsis).
 *
 * @param element Headsign text element carrying overflow truncation styles.
 * @returns `true` when the rendered text overflows its box.
 */
export function isVehicleIslandHeadsignTruncated( element: HTMLElement ): boolean {
    return element.scrollWidth > element.clientWidth + VEHICLE_ISLAND_HEADSIGN_TRUNCATION_TOLERANCE_PX;
}

/** Luminance threshold for text color selection */
const LUMINANCE_THRESHOLD = 0.5;

/** WCAG 2.1 luminance calculation constants */
const WCAG_NORMALIZATION_THRESHOLD = 0.03928;
const WCAG_LINEAR_DIVISOR = 12.92;
const WCAG_GAMMA_OFFSET = 0.055;
const WCAG_GAMMA_DIVISOR = 1.055;
const WCAG_GAMMA_POWER = 2.4;
const WCAG_RED_WEIGHT = 0.2126;
const WCAG_GREEN_WEIGHT = 0.7152;
const WCAG_BLUE_WEIGHT = 0.0722;

/**
 * Calculate relative luminance of a color (0-1) per WCAG 2.1.
 *
 * @param r Red component (0-255).
 * @param g Green component (0-255).
 * @param b Blue component (0-255).
 * @returns Relative luminance value (0-1).
 */
export const getRelativeLuminance = ( r: number, g: number, b: number ): number => {
    const [ rs, gs, bs ] = [ r, g, b ].map( ( val ) => {
        const normalized = val / 255;

        return normalized <= WCAG_NORMALIZATION_THRESHOLD
            ? normalized / WCAG_LINEAR_DIVISOR
            : Math.pow( ( normalized + WCAG_GAMMA_OFFSET ) / WCAG_GAMMA_DIVISOR, WCAG_GAMMA_POWER );
    } );

    return WCAG_RED_WEIGHT * rs + WCAG_GREEN_WEIGHT * gs + WCAG_BLUE_WEIGHT * bs;
};

/**
 * Determine whether text should be black or white for contrast on the vehicle color.
 *
 * @param color Vehicle color (RGB).
 * @returns `"black"` or `"white"` for best contrast.
 */
export const getVehicleInfoTextColor = ( color: PopupContent["color"] ): "black" | "white" => {
    if ( color === undefined ) {
        return "white";
    }

    const luminance = getRelativeLuminance( color.r, color.g, color.b );

    return luminance > LUMINANCE_THRESHOLD ? "black" : "white";
};

/**
 * Returns true when all RGB components are within a gray tolerance band.
 *
 * @param color Vehicle color (RGB).
 * @returns True if the color reads as gray.
 */
export const isGrayVehicleColor = ( color: PopupContent["color"] ): boolean => {
    if ( color === undefined ) {
        return false;
    }

    const maxDiff = Math.max(
        Math.abs( color.r - color.g ),
        Math.abs( color.g - color.b ),
        Math.abs( color.r - color.b ),
    );

    return maxDiff <= 30;
};

/**
 * Vehicle color as an opaque RGB string (keeps gray for line badge).
 *
 * @param color Vehicle color (RGB).
 * @returns CSS rgb() string.
 */
export const getVehicleColorRgb = ( color: PopupContent["color"] ): string => {
    if ( color === undefined ) {
        return `rgb(${ DEFAULT_PRIMARY_COLOR_R }, ${ DEFAULT_PRIMARY_COLOR_G }, ${ DEFAULT_PRIMARY_COLOR_B })`;
    }

    return `rgb(${ color.r }, ${ color.g }, ${ color.b })`;
};

/**
 * Vehicle color with transparency for backdrop blur surfaces.
 *
 * @param color Vehicle color (RGB).
 * @param opacity Opacity value (0-1).
 * @returns CSS rgba() string.
 */
export const getVehicleColorWithOpacity = (
    color: PopupContent["color"],
    opacity: number = DEFAULT_OPACITY,
): string => {
    if ( color === undefined || isGrayVehicleColor( color ) ) {
        return `rgba(${ DEFAULT_PRIMARY_COLOR_R }, ${ DEFAULT_PRIMARY_COLOR_G }, ${ DEFAULT_PRIMARY_COLOR_B }, ${ opacity })`;
    }

    return `rgba(${ color.r }, ${ color.g }, ${ color.b }, ${ opacity })`;
};

/** Resolved CSS color tokens for vehicle chrome surfaces and text. */
export interface VehicleChromeColorTokens {
    "badgeBackground":  string;
    "borderColor":      string;
    "headerBackground": string;
    "shellBackground":  string;
    "textColor":        string;
}

/**
 * French delay status label for the vehicle island subline.
 *
 * @param delaySec Signed delay in seconds (positive = late, negative = early).
 * @returns `"Retard de N min"`, `"Avance de N min"`, or `null` when rounded to zero minutes.
 */
export function formatVehicleDelayStatusLabel( delaySec: number ): string | null {
    const minutes = Math.abs( Math.round( delaySec / 60 ) );

    if ( minutes === 0 ) {
        return null;
    }

    if ( delaySec > 0 ) {
        return `Retard de ${ minutes } min`;
    }

    return `Avance de ${ minutes } min`;
}

/**
 * First badge status word for the vehicle island delay pill.
 *
 * @param delaySec Signed delay in seconds (positive = late, negative = early).
 * @param useCompactStatusLine When true, returns `"Ret."` / `"Av."`; otherwise `"Retard"` / `"Avance"`.
 * @returns Status word for the first badge line.
 */
export function formatVehicleDelayBadgeStatusLine(
    delaySec: number,
    useCompactStatusLine: boolean,
): string {
    if ( delaySec > 0 ) {
        return useCompactStatusLine ? "Ret." : "Retard";
    }

    return useCompactStatusLine ? "Av." : "Avance";
}

/**
 * Splits delay status into two badge lines: status word and duration.
 *
 * @param delaySec Signed delay in seconds (positive = late, negative = early).
 * @param useCompactStatusLine When true, abbreviates the status word on line 1.
 * @returns `"Ret."` / `"Av."` or `"Retard"` / `"Avance"` on line 1 and `"Nmn"` on line 2, or `null` when rounded to zero minutes.
 */
export function formatVehicleDelayBadgeLines(
    delaySec: number,
    useCompactStatusLine = true,
): {
    "statusLine": string;
    "valueLine":  string;
} | null {
    const minutes = Math.abs( Math.round( delaySec / 60 ) );

    if ( minutes === 0 ) {
        return null;
    }

    return {
        "statusLine": formatVehicleDelayBadgeStatusLine( delaySec, useCompactStatusLine ),
        "valueLine":  `${ minutes }mn`,
    };
}

/**
 * RT delay in seconds for island display, when present on popup content.
 *
 * @param content Vehicle popup content.
 * @returns Signed delay in seconds, or undefined when RT delay is absent.
 */
export function resolveEffectiveVehicleDelaySec( content: PopupContent ): number | undefined {
    return content.delaySec;
}

/**
 * Delay badge presentation for the vehicle island trailing cluster.
 */
export interface VehicleDelayBadgePresentation {

    /** Full label for accessibility, e.g. `"Retard de 3 min"`. */
    "label": string | null;

    /** First badge line: `"Ret."` / `"Av."` or `"Retard"` / `"Avance"`. */
    "statusLine": string | null;

    /** Second badge line: duration, e.g. `"3mn"`. */
    "valueLine":            string | null;
    "badgeBackgroundColor": string | null;
    "badgeBorderColor":     string | null;
    "badgeTextColor":       string | null;
}

/** @deprecated Use {@link VehicleDelayBadgePresentation}. */
export type VehicleDelaySublinePresentation = VehicleDelayBadgePresentation;

/**
 * Resolves trailing delay badge content and pill colors from popup content.
 *
 * @param content Vehicle popup content.
 * @param isDarkUi Whether dark UI palette is active.
 * @param options Optional presentation overrides.
 * @param options.useCompactStatusLine When true, abbreviates the status word on line 1.
 * @returns Badge lines and colors when delay is present and non-zero; otherwise null fields.
 */
export function resolveVehicleDelayBadgePresentation(
    content: PopupContent,
    isDarkUi: boolean,
    options?: { "useCompactStatusLine"?: boolean },
): VehicleDelayBadgePresentation {
    const emptyPresentation: VehicleDelayBadgePresentation = {
        "label":                null,
        "statusLine":           null,
        "valueLine":            null,
        "badgeBackgroundColor": null,
        "badgeBorderColor":     null,
        "badgeTextColor":       null,
    };
    const delaySec = resolveEffectiveVehicleDelaySec( content );

    if ( delaySec === undefined ) {
        return emptyPresentation;
    }

    const badgeLines = formatVehicleDelayBadgeLines(
        delaySec,
        options?.useCompactStatusLine ?? true,
    );
    const label = formatVehicleDelayStatusLabel( delaySec );

    if ( badgeLines === null || label === null ) {
        return emptyPresentation;
    }

    const badgePresentation = getMapStopArrivalTimePillPresentation(
        isDarkUi,
        delaySec,
        true,
        true,
    );

    return {
        label,
        "statusLine":           badgeLines.statusLine,
        "valueLine":            badgeLines.valueLine,
        "badgeBackgroundColor": badgePresentation.backgroundColor,
        "badgeBorderColor":     badgePresentation.borderColor,
        "badgeTextColor":       badgePresentation.textColor,
    };
}

/**
 * @deprecated Use {@link resolveVehicleDelayBadgePresentation}.
 */
export function resolveVehicleDelaySublinePresentation(
    content: PopupContent,
    isDarkUi: boolean,
): VehicleDelayBadgePresentation {
    return resolveVehicleDelayBadgePresentation( content, isDarkUi );
}

/**
 * Inline styles for the island delay badge from {@link resolveVehicleDelaySublinePresentation}.
 *
 * @param presentation Resolved delay badge presentation.
 * @returns Style object for the badge pill, or undefined when incomplete.
 */
export function resolveVehicleDelayBadgeStyle( presentation: VehicleDelayBadgePresentation ): CSSProperties | undefined {
    if (
        presentation.statusLine === null ||
        presentation.valueLine === null ||
        presentation.badgeBackgroundColor === null ||
        presentation.badgeBorderColor === null ||
        presentation.badgeTextColor === null
    ) {
        return undefined;
    }

    return {
        "backgroundColor": presentation.badgeBackgroundColor,
        "borderColor":     presentation.badgeBorderColor,
        "color":           presentation.badgeTextColor,
    };
}

/**
 * Resolves animatable color tokens for vehicle info chrome.
 *
 * @param content Vehicle popup content.
 * @returns CSS color strings for shell, header, badge, and text.
 */
export function resolveVehicleChromeColorTokens( content: PopupContent ): VehicleChromeColorTokens {
    const textColor = getVehicleInfoTextColor( content.color ) === "black" ? "#000000" : "#ffffff";

    return {
        "shellBackground":  getVehicleColorWithOpacity( content.color, VEHICLE_INFO_BACKGROUND_OPACITY ),
        "borderColor":      getVehicleColorWithOpacity( content.color, VEHICLE_INFO_HEADER_OPACITY ),
        "headerBackground": getVehicleColorWithOpacity( content.color, VEHICLE_INFO_HEADER_OPACITY ),
        "badgeBackground":  getVehicleColorRgb( content.color ),
        "textColor":        textColor,
    };
}

/**
 * Maps a France-specific route type code to its island subline label.
 *
 * @param code Raw `route_type_france` wire value.
 * @returns Display label, or `null` when the code must not be shown.
 */
export function mapRouteTypeFranceLabel( code: string | null | undefined ): string | null {
    if ( code === null || code === undefined ) {
        return null;
    }

    const normalizedCode = code.trim().toUpperCase();

    if ( normalizedCode === "" || ROUTE_TYPE_FRANCE_HIDDEN_CODES.has( normalizedCode ) ) {
        return null;
    }

    return ROUTE_TYPE_FRANCE_LABEL_BY_CODE[normalizedCode] ?? null;
}

/**
 * Returns the original trip headsign for the island subline when it adds information.
 *
 * @param tripHeadsign Display headsign from realtime schedule.
 * @param tripHeadsignOriginal Original headsign from realtime schedule.
 * @returns Trimmed original headsign, or `null` when it must not be shown.
 */
export function formatVehicleTripHeadsignOriginal(
    tripHeadsign: string | null | undefined,
    tripHeadsignOriginal: string | null | undefined,
): string | null {
    if ( tripHeadsignOriginal === null || tripHeadsignOriginal === undefined ) {
        return null;
    }

    const normalizedOriginal = tripHeadsignOriginal.trim();

    if ( normalizedOriginal === "" || normalizedOriginal === "0" ) {
        return null;
    }

    const normalizedHeadsign = tripHeadsign?.trim() ?? "";

    if ( normalizedHeadsign !== "" && normalizedOriginal === normalizedHeadsign ) {
        return null;
    }

    return normalizedOriginal;
}

/**
 * Builds the island center subline from route type and original headsign fields.
 *
 * @param routeTypeFrance France-specific route type code.
 * @param tripHeadsign Display trip headsign.
 * @param tripHeadsignOriginal Original trip headsign.
 * @returns Composed subline text, or `null` when every segment is hidden.
 */
export function formatVehicleIslandCenterSubline(
    routeTypeFrance: string | null | undefined,
    tripHeadsign: string | null | undefined,
    tripHeadsignOriginal: string | null | undefined,
): string | null {
    const segments = [
        mapRouteTypeFranceLabel( routeTypeFrance ),
        formatVehicleTripHeadsignOriginal( tripHeadsign, tripHeadsignOriginal ),
    ].filter( ( segment ): segment is string => segment !== null );

    if ( segments.length === 0 ) {
        return null;
    }

    return segments.join( VEHICLE_ISLAND_CENTER_SUBLINE_SEPARATOR );
}
